/**
 * "You're at OZORA — Enter" (plan.md step 59).
 *
 * In festival mode, the app's home asks the device which rave this person is
 * at (`pickFestival`, `@myrave/shared`): one clear match OFFERS it — "You're
 * at OZORA — Enter" — two ask, none leaves the page alone. It never opens the
 * rave by itself (the user, 2026-10-07: after signing in the app landed on
 * OZORA instead of My raves — every sign-in is a new page load, so "once per
 * launch" was every time). Location is used only if they turned it on here, and only on this
 * phone: it is compared with the sites already stored on the device and is
 * never sent anywhere.
 */
import { useEffect, useState } from 'react';
import { pickFestival, type FestivalPick } from '@myrave/shared';
import { useT } from '@/i18n';
import { Button, Card, Row, Stack } from '@/ui';
import { leaveTo } from './AuthProvider';
import { festivalUrl } from '@/lib/tenant';
import type { MyFestival } from '@/lib/api';
import {
  currentPlace, festivalCandidates, festivalLocationOn, inFestivalMode, setFestivalLocation,
} from '@/lib/festivalMode';
import './FestivalPicker.css';

/** Kept for the tests that still call it; nothing opens by itself any more. */
export function resetFestivalPickerForTest(): void {}

export function FestivalPicker({ attending, now = new Date() }: { attending: readonly MyFestival[]; now?: Date }) {
  const tr = useT();
  const [pick, setPick] = useState<FestivalPick | null>(null);
  const [useLocation, setUseLocation] = useState(festivalLocationOn);
  const on = inFestivalMode();

  useEffect(() => {
    if (!on) return;
    let cancelled = false;
    void (async () => {
      const candidates = await festivalCandidates(attending);
      const here = useLocation ? await currentPlace() : null;
      const result = pickFestival(candidates, now, here);
      if (cancelled) return;
      setPick(result);
    })();
    return () => { cancelled = true; };
    // `now` is a render-time value: the pick is made once per mount.
  }, [on, attending, useLocation]);

  if (!on || !pick || pick.kind === 'none') return null;
  const enter = (c: { subdomain: string; editionSlug: string }) => void leaveTo(festivalUrl(c.subdomain, c.editionSlug));

  return (
    <Card className="festival-picker">
      {pick.kind === 'one' ? (
        <Row gap={10} className="festival-picker-row">
          <span className="festival-picker-title">{tr("You're at {name}", { name: pick.candidate.name })}</span>
          <Button onClick={() => enter(pick.candidate)}>{tr('Enter')}</Button>
        </Row>
      ) : (
        <Stack gap={10}>
          <span className="festival-picker-title">{tr('Which festival are you at?')}</span>
          <Row gap={8} className="festival-picker-row">
            {pick.candidates.map((c) => (
              <Button key={c.editionId} variant="secondary" onClick={() => enter(c)}>{c.name}</Button>
            ))}
          </Row>
          {!useLocation && (
            <Button variant="quiet" onClick={() => { setFestivalLocation(true); setUseLocation(true); }}>
              {tr('Use my location to tell — it stays on this phone')}
            </Button>
          )}
        </Stack>
      )}
    </Card>
  );
}
